import "./styles/index.css";
import "./i18n";

import { LightboxProvider } from "@sphtech/idpf-lightbox-react";
import { captureConsoleIntegration, init as SentryInit } from "@sentry/react";
import { NeuronProvider, SPAConfig } from "@sphtech/neuron";
import { ReactElement, StrictMode, useEffect, useLayoutEffect } from "react";
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLocation,
  useMatches,
} from "react-router";

import neuronModules from "../neuron.config.json";
import { initGTM } from "./analytics/gtm";
import AppContextHOC from "./components/AppContextHOC";
import { getLightboxConfig } from "./config/lightboxConfig";
import { PUB_CONFIGS } from "./config/lightboxPubData";
import Hotjar from "./providers/Hotjar";
import NewRelic from "./providers/NewRelic";
import Vwo from "./providers/Vwo";

const LIGHTBOX2_JS_URL = import.meta.env.VITE_MYSPH_LIGHTBOX2_JS ?? "";
const MYSPH_CLIENT_ID = import.meta.env.VITE_MYSPH_CLIENT_ID ?? "";

// Derive inline script config from shared PUB_CONFIGS — single source of truth.
const LB_PUB_CONFIGS = JSON.stringify(
  Object.fromEntries(
    Object.entries(PUB_CONFIGS).map(([key, cfg]) => [
      key,
      { pubId: cfg.pubId, lang: cfg.language, colors: cfg.colors, logo: cfg.logo },
    ]),
  ),
);

SentryInit({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  maxBreadcrumbs: 50,
  integrations: [captureConsoleIntegration({ levels: ["error"] })],
  environment: import.meta.env.VITE_ENVIRONMENT,
  enabled: import.meta.env.MODE !== "development",
  sampleRate: 0.05,
});

const neuronConfig: SPAConfig = {
  sessionApiUrl: import.meta.env.VITE_NEURON_SESSION_API_URL,
  sessionApiToken: import.meta.env.VITE_NEURON_SESSION_API_TOKEN,
  segmentsApiUrl: import.meta.env.VITE_SEGMENTO_API_URL,
  ...neuronModules,
};

// Layout replaces index.html — renders the <html> shell
export function Layout({ children }: { children: React.ReactNode }) {
  const matches = useMatches();

  // Skip <Scripts /> for static error pages (hydrate: false)
  const includeScripts = matches.every((match) => {
    const handle = match.handle as { hydrate?: boolean } | undefined;
    return handle?.hydrate == null || handle.hydrate;
  });

  // Read lang from the matched route's loader data, fall back to "en"
  const lang = matches.reduce<string>((acc, match) => {
    const data = match.data as { lang?: string } | undefined;
    return data?.lang ?? acc;
  }, "en");

  return (
    <html lang={lang}>
      <head>
        <meta charSet="UTF-8" />
        <link href="/favicon.ico" rel="icon" type="image/x-icon" />
        <meta content="width" name="MobileOptimized" />
        <meta content="true" name="HandheldFriendly" />
        <meta content="width=device-width, initial-scale=1" name="viewport" />
        <meta content="ie=edge" httpEquiv="x-ua-compatible" />
        <meta
          name="google-site-verification"
          content="WJz_lO68TW2Vfo18N9JuDjBEwzxidEuMd5BCQg0SWwM"
        />
        <script
          dangerouslySetInnerHTML={{
            __html: "window.dataLayer = window.dataLayer || [];",
          }}
        />

        {/* Lightbox 2.0: Client config with per-publication theme.
            Runs synchronously before the deferred module script loads,
            so the correct pubId/theme is ready when the Lightbox reads it. */}
        <script
          dangerouslySetInnerHTML={{
            __html: `(function() {
  var configs = ${LB_PUB_CONFIGS};
  var seg = window.location.pathname.substring(1).split("/")[0] || "";
  var pub = seg.replace(/-single$/, "");
  var cfg = configs[pub] || configs["st"];
  var logoUrl = cfg.logo ? (window.location.origin + "/lightbox/" + cfg.logo) : "";
  window.mySPHConfig = {
    clientId: "${MYSPH_CLIENT_ID}",
    pubId: cfg.pubId,
    language: cfg.lang,
    providers: ["google", "facebook", "googleOneTap", "apple"],
    uxMode: "modal",
    ssoMode: "manual",
    theme: {
      colors: cfg.colors,
      bannerImageURLs: logoUrl ? { "banner-url": logoUrl } : {}
    }
  };
})();`,
          }}
        />

        <Meta />
        <Links />
      </head>
      <body>
        {/* Lightbox 2.0 mount point */}
        <div id="mySPHLightbox"></div>
        <div id="app">{children}</div>
        <ScrollRestoration />
        {includeScripts ? <Scripts /> : null}
      </body>
    </html>
  );
}

// App wraps all routes with providers (replaces App.tsx)
export default function App(): ReactElement {
  const matches = useMatches();
  const location = useLocation();

  const isStaticPage = matches.some((match) => {
    const handle = match.handle as { hydrate?: boolean } | undefined;
    return handle?.hydrate === false;
  });

  // Extract pubId from route params for per-publication Lightbox config
  const pubMatch = matches.find(
    (m) => (m.params as Record<string, string>)?.pub,
  );
  const pubId = (pubMatch?.params as Record<string, string>)?.pub;
  const lbConfig = getLightboxConfig(pubId);

  // Update window.mySPHConfig with correct publication theme before paint.
  // Layout sets a default config; this overrides it per-publication.
  // useLayoutEffect fires before browser paint and before LightboxProvider's
  // useEffect, ensuring the correct config is available when Lightbox initializes.
  const isConcurrencyPage = location.pathname === "/concurrency";

  useLayoutEffect(() => {
    window.mySPHConfig = {
      ...window.mySPHConfig,
      pubId: lbConfig.pubId,
      language: lbConfig.language,
      theme: lbConfig.theme,
      uxMode: isConcurrencyPage ? "embedded" : "modal",
    };
  }, [lbConfig, isConcurrencyPage]);

  // Load Lightbox bundle AFTER React hydration to prevent React from
  // clearing the Lightbox DOM during hydration (race condition).
  useEffect(() => {
    const jsUrl = LIGHTBOX2_JS_URL;
    if (jsUrl && !document.querySelector(`script[src="${jsUrl}"]`)) {
      const s = document.createElement("script");
      s.type = "module";
      s.src = jsUrl;
      document.head.appendChild(s);
    }
  }, []);

  useEffect(() => {
    initGTM();
  }, []);

  if (isStaticPage) {
    return <Outlet />;
  }

  // Skip providers for OAuth callback — Lightbox handles the flow via script
  // tags in Layout. Wrapping with LightboxProvider can interfere.
  if (location.pathname === "/oauth/callback") {
    return <Outlet />;
  }

  return (
    <StrictMode>
      <NewRelic />
      <Hotjar />
      <Vwo />
      <LightboxProvider>
        <AppContextHOC>
          <NeuronProvider config={neuronConfig}>
            <Outlet />
          </NeuronProvider>
        </AppContextHOC>
      </LightboxProvider>
    </StrictMode>
  );
}

export function ErrorBoundary({ error }: { error: unknown }) {
  if (isRouteErrorResponse(error)) {
    return (
      <div>
        <h1>
          {error.status} {error.statusText}
        </h1>
        <p>{error.data}</p>
      </div>
    );
  } else if (error instanceof Error) {
    return (
      <div>
        <h1>Error</h1>
        <p>{error.message}</p>
        <pre>{error.stack}</pre>
      </div>
    );
  }
  return <h1>Unknown Error</h1>;
}
